import { useFieldArray, useFormContext } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import type { Asset } from 'apiTypes/Asset'
import { AssetAddedBy } from 'apiTypes/AssetAddedBy'
import { AssetType } from 'apiTypes/AssetType'
import { PropertyType } from 'apiTypes/PropertyType'
import { VehicleOwnershipStatus } from 'apiTypes/VehicleOwnershipStatus'
import { VehicleType } from 'apiTypes/VehicleType'

import { ButtonGroup, ButtonLink, ButtonSolidSubmit } from 'components/Buttons'
import { IconPlus } from 'components/Icons'

import { PropertyForm } from './PropertyForm'
import { VehicleForm } from './VehicleForm'

export type Model = {
	assets: Array<Asset>
}

type Props = {
	hasAdvisorPrefilledAssets: boolean
}

export const AssetsForm = ({ hasAdvisorPrefilledAssets }: Props) => {
	const { control } = useFormContext<Model>()
	const { fields, append, remove } = useFieldArray({
		control,
		name: 'assets',
	})

	return (
		<>
			{hasAdvisorPrefilledAssets ? (
				<p>
					<FormattedMessage
						id='flow.assets.prefilledDescription'
						defaultMessage='Your advisor has already added assets to your budget. Review them below and continue when you are ready.'
					/>
				</p>
			) : (
				<p>
					<FormattedMessage
						id='flow.assets.description'
						defaultMessage='Add any housing or transport assets that should be included in your budget. This step is optional.'
					/>
				</p>
			)}
			{fields.map(({ addedBy, id, type }, index) =>
				type === AssetType.Property ? (
					<PropertyForm
						key={id}
						index={index}
						isReadOnly={addedBy === AssetAddedBy.Advisor}
						onRemove={() => remove(index)}
					/>
				) : (
					<VehicleForm
						key={id}
						index={index}
						isReadOnly={addedBy === AssetAddedBy.Advisor}
						onRemove={() => remove(index)}
					/>
				),
			)}
			<ButtonGroup>
				<ButtonLink
					label={
						<>
							<IconPlus />
							<FormattedMessage id='flow.assets.buttonAddProperty' defaultMessage='Add residence' />
						</>
					}
					onClick={() =>
						append({
							addedBy: AssetAddedBy.Applicant,
							type: AssetType.Property,
							propertyType: PropertyType.House,
							address: '',
						})
					}
				/>
				<ButtonLink
					label={
						<>
							<IconPlus />
							<FormattedMessage id='flow.assets.buttonAddVehicle' defaultMessage='Add means of transport' />
						</>
					}
					onClick={() =>
						append({
							addedBy: AssetAddedBy.Applicant,
							type: AssetType.Vehicle,
							vehicleType: VehicleType.Car,
							licensePlate: '',
							ownershipStatus: VehicleOwnershipStatus.Owner,
						})
					}
				/>
				<ButtonSolidSubmit label={<FormattedMessage id='flow.buttonNext' defaultMessage='Continue' />} />
			</ButtonGroup>
		</>
	)
}
